import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { authApi, clearToken, getToken, setToken } from '../lib/api';
import { decodeJwtPayload } from '../lib/jwt';

interface Session {
  token: string;
  role: 'employee' | 'manager';
  sub: string;
}

interface SessionContextValue {
  session: Session | null;
  loginEmployee: (telefone: string, pin: string) => Promise<{ employee: any }>;
  loginManager: (password: string) => Promise<void>;
  resetPin: (cpf: string, telefone: string, novoPin: string) => Promise<{ employee: any }>;
  logout: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function restoreSession(): Session | null {
  const token = getToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) { clearToken(); return null; }
  return { token, role: payload.role, sub: payload.sub };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => restoreSession());

  const loginEmployee = useCallback(async (telefone: string, pin: string) => {
    const res = await authApi.loginEmployee(telefone, pin);
    setToken(res.token);
    const payload = decodeJwtPayload(res.token)!;
    setSession({ token: res.token, role: payload.role, sub: payload.sub });
    return { employee: res.employee };
  }, []);

  const loginManager = useCallback(async (password: string) => {
    const res = await authApi.loginManager(password);
    setToken(res.token);
    const payload = decodeJwtPayload(res.token)!;
    setSession({ token: res.token, role: payload.role, sub: payload.sub });
  }, []);

  const resetPin = useCallback(async (cpf: string, telefone: string, novoPin: string) => {
    const res = await authApi.resetPin(cpf, telefone, novoPin);
    setToken(res.token);
    const payload = decodeJwtPayload(res.token)!;
    setSession({ token: res.token, role: payload.role, sub: payload.sub });
    return { employee: res.employee };
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setSession(null);
  }, []);

  return (
    <SessionContext.Provider value={{ session, loginEmployee, loginManager, resetPin, logout }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
